import moment from "moment";

import { DateFormat } from "@app/core/i18n/constants";

export type SimplifiedDateInput = string | Date;

export const toFormat = (date: SimplifiedDateInput, outputFormat: string) => moment(date).format(outputFormat);

export const addDays = (date: SimplifiedDateInput, days: number, outputFormat?: string, inputFormat?: string) =>
    moment(date, inputFormat).add(days, "days").format(outputFormat);

export const addMonths = (date: SimplifiedDateInput, months: number, outputFormat?: string, inputFormat?: string) =>
    moment(date, inputFormat).add(months, "months").format(outputFormat);

export const addWeeks = (date: SimplifiedDateInput, weeks: number, outputFormat?: string, inputFormat?: string) =>
    moment(date, inputFormat).add(weeks, "weeks").format(outputFormat);

export const addYears = (date: SimplifiedDateInput, years: number, outputFormat?: string, inputFormat?: string) =>
    moment(date, inputFormat).add(years, "years").format(outputFormat);

export const formatDateFrom = (date: SimplifiedDateInput, inputFormat: string) => {
    const parsedDate = moment(date, inputFormat, true);
    return parsedDate ? parsedDate.toDate() : null;
};

export function formatDisplayDateForApi(date: SimplifiedDateInput, inputFormat: string) {
    const momentDate = moment(date, inputFormat);

    return momentDate.isValid() ? momentDate.local().format(DateFormat.ApiFormat) : null;
}

export const getCurrentYear = (today?: Date): number => (today ?? new Date()).getFullYear();

export const formatUtcDate = (date: SimplifiedDateInput, outputFormat: string, inputFormat?: string) =>
    moment.utc(date, inputFormat).format(outputFormat);

export const formatLocalDate = (date: SimplifiedDateInput, outputFormat: string, inputFormat?: string) => {
    return moment(date, inputFormat).local().format(outputFormat);
};

export const getToday = (outputFormat?: string) => moment().format(outputFormat);

export const getMonth = (date: SimplifiedDateInput) => moment(date).month();

export const getMonths = () => moment.months();

export const getMonthString = (month: number) => moment.months(month);

export const getYear = (date: SimplifiedDateInput, inputFormat?: string) => moment(date, inputFormat).year();

export const getCompletedMonths = (year: number = getCurrentYear()): string[] => {
    const today = getToday();

    if (year === getYear(today)) {
        const currentMonth = getMonth(today);
        const months: string[] = [];

        for (let i = 0; i < currentMonth; i++) {
            months.push(getMonthString(i));
        }

        return months;
    }

    return getMonths();
};

export const getCurrentQuarter = (today?: Date) => Math.floor(((today ?? new Date()).getMonth() + 3) / 3);

export const getCompletedQuarters = (today?: Date): number[] => {
    const trimesterNumber = getCurrentQuarter(today ?? new Date());
    const trimesters = Array.from({ length: trimesterNumber - 1 }, (_, i) => i + 1);

    return trimesters;
};

export const getDateFromMonthsAgo = (months: number, outputFormat?: string) => moment().subtract(months, "months").format(outputFormat);

export const getDay = (date: SimplifiedDateInput, inputFormat?: string) => moment(date, inputFormat).date();

export const getEndOfTrimester = (trimester: number, year: number = getCurrentYear(), outputFormat: string) => {
    let endMonth = trimester * 3 + 1;

    if (endMonth > 12) {
        year += 1;
        endMonth = 1;
    }

    return moment(`01-${endMonth}-${year}`, "dd-MM-YYYY").subtract(1, "days").format(outputFormat);
};

export const getLastNYears = (goBackTo: number, startYear = getCurrentYear()) =>
    Array(goBackTo)
        .fill(startYear, 0)
        .map((date, index) => date - index);

export const getStartOfWeek = (date: SimplifiedDateInput, outputFormat?: string, inputFormat?: string) =>
    moment(date, inputFormat).startOf("week").format(outputFormat);

export const getEndOfWeek = (date: SimplifiedDateInput, outputFormat?: string, inputFormat?: string) =>
    moment(date, inputFormat).endOf("week").format(outputFormat);

export const getStartOfLastWeek = (date: SimplifiedDateInput, outputFormat?: string, inputFormat?: string) =>
    moment(date, inputFormat).startOf("week").subtract(1, "weeks").format(outputFormat);

export const getEndOfLastWeek = (date: SimplifiedDateInput, outputFormat?: string, inputFormat?: string) =>
    moment(date, inputFormat).endOf("week").subtract(1, "weeks").format(outputFormat);

export const getStartOfMonth = (date: SimplifiedDateInput, outputFormat?: string, inputFormat?: string) =>
    moment(date, inputFormat).startOf("month").format(outputFormat);

export const getEndOfMonth = (date: SimplifiedDateInput, outputFormat?: string, inputFormat?: string) =>
    moment(date, inputFormat).endOf("month").format(outputFormat);

export const getStartOfYear = (date: SimplifiedDateInput, outputFormat?: string, inputFormat?: string) =>
    moment(date, inputFormat).startOf("year").format(outputFormat);

export const getEndOfYear = (date: SimplifiedDateInput, outputFormat?: string, inputFormat?: string) =>
    moment(date, inputFormat).endOf("year").format(outputFormat);

export function getUnixTimestamp(date: SimplifiedDateInput, inputFormat: string) {
    const momentDate = moment(date, inputFormat);

    return momentDate.isValid() ? momentDate.unix() : null;
}

export function getCurrentTimestamp() {
    return moment().unix();
}

export const getYearDiffWithToday = (date: SimplifiedDateInput) => moment().diff(date, "years");

export const getDayDiffWithToday = (date: SimplifiedDateInput) => moment().diff(date, "days");

export const formatDateFromMonthAndYear = (month: number | string, year: number | string, outputFormat?: string) =>
    moment(`${year}-${month}`, "YYYY-MM").format(outputFormat);

export const isBefore = (date: SimplifiedDateInput, secondDate: SimplifiedDateInput, inputFormat?: string) =>
    moment(date, inputFormat).isBefore(secondDate);

export const isAfter = (date: SimplifiedDateInput, secondDate: SimplifiedDateInput, inputFormat?: string) =>
    moment(date, inputFormat).isAfter(secondDate);

export const isDateBeforeNow = (date: SimplifiedDateInput, inputFormat?: string) => moment(date, inputFormat).isBefore(moment());

export const isDateValid = (date: SimplifiedDateInput, inputFormat?: string) => moment(date, inputFormat, /* strict parsing */ true).isValid();

export const isSameOrBefore = (date: SimplifiedDateInput, secondDate: SimplifiedDateInput, inputFormat?: string, secondInputFormat?: string) =>
    moment(date, inputFormat).isSameOrBefore(moment(secondDate, secondInputFormat));

export const momentUtc = (date: SimplifiedDateInput, inputFormat?: string, outputFormat?: string) =>
    moment.utc(date, inputFormat).format(outputFormat);

export const setGlobalMomentLocale = (locale: string) => moment.locale(locale);

export const setLocalMomentLocale = (date: SimplifiedDateInput, locale: string) => moment(date).locale(locale).format();

export const subtractDays = (date: SimplifiedDateInput, days: number, outputFormat?: string, inputFormat?: string) =>
    moment(date, inputFormat).subtract(days, "days").format(outputFormat);

export const subtractMonths = (date: SimplifiedDateInput, months: number, outputFormat?: string, inputFormat?: string) =>
    moment(date, inputFormat).subtract(months, "months").format(outputFormat);

export const subtractWeeks = (date: SimplifiedDateInput, weeks: number, outputFormat?: string, inputFormat?: string) =>
    moment(date, inputFormat).subtract(weeks, "weeks").format(outputFormat);

export const subtractYears = (date: SimplifiedDateInput, years: number, outputFormat?: string, inputFormat?: string) =>
    moment(date, inputFormat).subtract(years, "years").format(outputFormat);

export const toDate = (date: SimplifiedDateInput, inputFormat?: string) => {
    const momentDate = moment(date, inputFormat, true);

    return momentDate.isValid() ? momentDate.toDate() : null;
};

export const toISOString = (date: SimplifiedDateInput, inputFormat?: string) => moment(date, inputFormat).toISOString();

export const parseDateInterval = (dateInterval: DateIntervalDefinition): Date => {
    switch (dateInterval.type) {
        case "day":
            return moment(new Date(Date.now())).subtract(dateInterval.interval, "days").toDate();
        case "week":
            return moment(new Date(Date.now())).subtract(dateInterval.interval, "weeks").toDate();
        case "month":
            return moment(new Date(Date.now())).subtract(dateInterval.interval, "months").toDate();
        case "year":
            return moment(new Date(Date.now())).subtract(dateInterval.interval, "years").toDate();
    }
};

export const handleKeyPressForDateInput = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const { key } = event;

    if (!/[0-9/-]/.test(key)) {
        event.preventDefault();
    }
};

interface DurationFormatOptions {
    yearLabel?: { singular: string; plural: string };
    monthLabel?: { singular: string; plural: string };
    dayLabel?: { singular: string; plural: string };
    abbreviated?: boolean;
}

export const getFormattedDurationWithToday = (date: SimplifiedDateInput, options?: DurationFormatOptions): string => {
    const {
        yearLabel = { singular: "year", plural: "years" },
        monthLabel = { singular: "month", plural: "months" },
        dayLabel = { singular: "day", plural: "days" },
        abbreviated = false,
    } = options || {};

    const today = moment();
    const inputDate = moment(date);
    const duration = moment.duration(today.diff(inputDate));

    const years = duration.years();
    const months = duration.months();
    const days = duration.days();

    if (years > 0) {
        return `${years}${abbreviated ? "y" : years > 1 ? ` ${yearLabel.plural}` : ` ${yearLabel.singular}`}`;
    } else if (months > 0) {
        return `${months}${abbreviated ? "m" : months > 1 ? ` ${monthLabel.plural}` : ` ${monthLabel.singular}`}`;
    } else {
        return `${days}${abbreviated ? "d" : days > 1 ? ` ${dayLabel.plural}` : ` ${dayLabel.singular}`}`;
    }
};
